// SPDX-License-Identifier: LicenseRef-MMS-WIRE
// Copyright (c) 2016-2026 Metal Management Solutions (Pty) Ltd

import {
    EnvironmentProviders,
    ErrorHandler,
    inject,
    Injectable,
    InjectionToken,
    isDevMode,
    makeEnvironmentProviders,
    provideEnvironmentInitializer,
} from '@angular/core';
import {NavigationEnd, NavigationError, Router, withNavigationErrorHandler} from '@angular/router';
import {filter} from 'rxjs/operators';

export const CHUNK_RELOAD_KEY = 'wire-chunk-reload';

export const CHUNK_RELOAD_GUARD_MS = 60_000;

const DEFER_LOADING_FAILED = -750;

const CHUNK_LOAD_ERROR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError/;

export const PAGE_RELOAD = new InjectionToken<() => void>('PAGE_RELOAD', {
    providedIn: 'root',
    factory: () => () => window.location.reload(),
});

export function isChunkLoadError(error: any): boolean {
    const cause = error?.rejection ?? error?.originalError ?? error;
    return cause?.code === DEFER_LOADING_FAILED || CHUNK_LOAD_ERROR.test(`${cause?.name ?? ''} ${cause?.message ?? cause ?? ''}`);
}

export function reloadOnce(error: any, reload: () => void): boolean {
    if (!isChunkLoadError(error)) {
        return false;
    }
    try {
        const reloadedAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
        if (Date.now() - reloadedAt < CHUNK_RELOAD_GUARD_MS) {
            return false;
        }
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    } catch {
        return false;
    }
    reload();
    return true;
}

export function clearChunkReloadGuard(): void {
    try {
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch {
    }
}

export const reloadOnceOnChunkError = withNavigationErrorHandler((event: NavigationError) => {
    reloadOnce(event.error, inject(PAGE_RELOAD));
});

@Injectable()
export class ChunkReloadErrorHandler extends ErrorHandler {
    private reload = inject(PAGE_RELOAD);

    override handleError(error: any): void {
        if (!reloadOnce(error, this.reload)) {
            super.handleError(error);
        }
    }
}

export function provideChunkReload(): EnvironmentProviders {
    return makeEnvironmentProviders([
        {provide: ErrorHandler, useClass: ChunkReloadErrorHandler},
        provideEnvironmentInitializer(() => {
            if (isDevMode() && !(inject(ErrorHandler) instanceof ChunkReloadErrorHandler)) {
                throw new Error('A provider after provideChunkReload() replaced ChunkReloadErrorHandler, so a missing chunk outside navigation will not reload the page.');
            }
            inject(Router).events.pipe(
                filter(event => event instanceof NavigationEnd && event.id > 1)
            ).subscribe(() => clearChunkReloadGuard());
        }),
    ]);
}
